import { normalizeAnalysisJobId, publicResultForPresentation, readAnalysisJobState } from "./analysisResultContract.js";

const API_BASE = import.meta.env.VITE_API_BASE || "";
export const ANALYSIS_SUBMISSION_ENABLED = import.meta.env.VITE_ANALYSIS_SUBMISSION_ENABLED === "true";

async function json(response) {
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    const error = new Error(detail.detail || `Erreur HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

export async function getSurahs(signal) {
  return json(await fetch(`${API_BASE}/api/surahs`, { signal }));
}

export async function getLearningSurahs(profile = "child", signal) {
  const query = new URLSearchParams({ profile });
  return json(await fetch(`${API_BASE}/api/learning/surahs?${query}`, { signal }));
}

export async function getProfessorSessions(adminToken, signal) {
  const token = String(adminToken || "").trim();
  if (!token) throw new Error("Saisissez le code professeur.");

  const payload = await json(await fetch(
    `${API_BASE}/api/admin/react/sessions?limit=100`,
    {
      method: "GET",
      headers: { "X-Admin-Token": token },
      signal,
    },
  ));

  return {
    read_only: payload?.read_only === true,
    items: Array.isArray(payload?.items) ? payload.items : [],
  };
}


export async function getProfessorSessionDetail(sessionId, adminToken, signal) {
  const normalizedId = String(sessionId || "").trim();
  const token = String(adminToken || "").trim();
  if (!normalizedId) throw new Error("Séance professeur invalide.");
  if (!token) throw new Error("La consultation privée a expiré.");

  const payload = await json(await fetch(
    `${API_BASE}/api/admin/react/sessions/${encodeURIComponent(normalizedId)}`,
    {
      method: "GET",
      headers: { "X-Admin-Token": token },
      signal,
    },
  ));

  if (payload?.read_only !== true || !payload?.item) {
    throw new Error("La protection en lecture seule n’a pas été confirmée.");
  }
  return payload.item;
}

export async function submitRecitation({ audio, surahKey, endAyah }) {
  if (!ANALYSIS_SUBMISSION_ENABLED) {
    throw new Error("Interface React en validation — l’envoi audio reste désactivé jusqu’à l’isolation du staging.");
  }

  const body = new FormData();
  body.append("audio", audio, "recitation.webm");
  body.append("surah_key", surahKey);
  if (endAyah) body.append("recitation_end_ayah", String(endAyah));
  return json(await fetch(`${API_BASE}/api/react-analysis/jobs`, { method: "POST", body }));
}

export async function submitTargetedRetry({
  audio,
  sessionId,
  targetText,
  targetRule = "",
}) {
  if (!ANALYSIS_SUBMISSION_ENABLED) {
    throw new Error("Interface React en validation — la répétition audio reste désactivée.");
  }

  const normalizedSessionId = normalizeAnalysisJobId(sessionId);
  const normalizedTargetText = String(targetText || "").trim();
  if (!normalizedTargetText) {
    throw new Error("Segment de correction invalide");
  }

  const body = new FormData();
  body.append("audio", audio, "retry.webm");
  body.append("session_id", normalizedSessionId);
  body.append("target_text", normalizedTargetText);
  body.append("target_rule", String(targetRule || "").trim());

  return json(await fetch(
    `${API_BASE}/api/react-analysis/retry`,
    { method: "POST", body },
  ));
}

export async function getAnalysisJob(jobId, signal) {
  const normalizedId = normalizeAnalysisJobId(jobId);
  return json(await fetch(
    `${API_BASE}/api/react-analysis/jobs/${encodeURIComponent(normalizedId)}`,
    { method: "GET", signal },
  ));
}

export async function waitForResult(jobId, onProgress, signal) {
  let consecutiveTransientFailures = 0;

  for (let attempt = 0; attempt < 120; attempt += 1) {
    let payload;
    try {
      payload = await getAnalysisJob(jobId, signal);
      consecutiveTransientFailures = 0;
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") throw error;

      const temporaryGatewayFailure = [502, 503, 504].includes(error?.status);
      const temporaryNetworkFailure = error instanceof TypeError;
      if (!temporaryGatewayFailure && !temporaryNetworkFailure) throw error;

      consecutiveTransientFailures += 1;
      if (consecutiveTransientFailures > 12) {
        throw new Error("Le serveur met trop de temps à revenir. Votre analyse reste enregistrée ; réessayez dans quelques instants.");
      }

      onProgress?.({ state: "processing", temporaryInterruption: true });
      await new Promise((resolve) => setTimeout(resolve, 1500));
      continue;
    }

    const job = readAnalysisJobState(payload);
    onProgress?.(job);

    if (job.state === "completed") {
      return {
        ...publicResultForPresentation(payload),
        job_id: normalizeAnalysisJobId(jobId),
      };
    }
    if (job.state === "failed") {
      throw new Error(job.error.message);
    }

    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  throw new Error("L’analyse continue en arrière-plan. Revenez dans quelques instants.");
}
